import { useEffect, useState, useCallback } from 'react';
import { NBXStorage } from '../storage';
import { authTypes, LANG, uiTypes, NotificationInternalParams } from './eventTypes';
import { AccountClaims, Claims, Session, UserAccount, User } from '../types';
// TODO: is this all the subscribe events
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const parseJSONOrNull = (storedValue: string | null): any | null => {
  if (storedValue === null) return null;
  return JSON.parse(storedValue);
};
export function useAuthUser(): User | null | undefined {
  const [user, setUser] = useState<User | null | undefined>(undefined);

  useEffect(() => {
    NBXStorage.getItem('user').then(initialUser => {
      setUser(parseJSONOrNull(initialUser));
    });
    function userChanged(e): void {
      setUser(e.detail);
    }
    window.addEventListener(authTypes.AUTH_USER_CHANGED, userChanged);
    return (): void => window.removeEventListener(authTypes.AUTH_USER_CHANGED, userChanged);
  }, []);

  return user;
}
export function useAuthSession(): Session | null | undefined {
  const [session, setSession] = useState<Session | null | undefined>(undefined);

  useEffect(() => {
    async function init(): Promise<void> {
      const initialSession = await NBXStorage.getItem('session');
      setSession(parseJSONOrNull(initialSession));
    }

    function sessionChanged(e): void {
      setSession(e.detail);
    }

    init();
    window.addEventListener(authTypes.AUTH_SESSION_CHANGED, sessionChanged);
    return (): void => window.removeEventListener(authTypes.AUTH_SESSION_CHANGED, sessionChanged);
  }, []);

  return session;
}

export function useAuthUserClaims(userId?: string): Claims | null | undefined {
  const [userClaims, setUserClaims] = useState<Claims | null | undefined>(undefined);

  async function init(): Promise<void> {
    const initialClaims = await NBXStorage.getItem('claims');
    const claims = parseJSONOrNull(initialClaims);
    if (claims && userId) {
      if (claims.roles.includes('KYC'))
        NBXStorage.removeItem(`KYC-ACCEPTED-${userId}`, { useLocalStorage: true });
      const userKYCAccepted = await NBXStorage.getItem(`KYC-ACCEPTED-${userId}`, { useLocalStorage: true });
      if (userKYCAccepted) claims.roles.push('KYC-ACCEPTED');
      setUserClaims(claims);
    } else if (claims) {
      setUserClaims(claims);
    } else {
      setUserClaims(null);
    }
  }

  useEffect(() => {
    function sessionChanged(e): void {
      setUserClaims(e.detail);
    }

    init();
    window.addEventListener(authTypes.AUTH_USER_CLAIMS_CHANGED, sessionChanged);
    return (): void => window.removeEventListener(authTypes.AUTH_USER_CLAIMS_CHANGED, sessionChanged);
  }, []);

  return userClaims;
}

export function useAuthAccountClaims(): AccountClaims | null | undefined {
  const [accountClaims, setAccountClaims] = useState<AccountClaims | null | undefined>(undefined);

  async function init(): Promise<void> {
    const initialAccountClaims = await NBXStorage.getItem('account-claims');
    setAccountClaims(parseJSONOrNull(initialAccountClaims));
  }

  useEffect(() => {
    function sessionChanged(e): void {
      setAccountClaims(e.detail);
    }

    init();
    window.addEventListener(authTypes.AUTH_ACCOUNT_CLAIMS_CHANGED, sessionChanged);
    return (): void => window.removeEventListener(authTypes.AUTH_ACCOUNT_CLAIMS_CHANGED, sessionChanged);
  }, []);

  return accountClaims;
}

export function useAuthAccountId(): string | null | undefined {
  const [accountId, setAccountId] = useState<string | null | undefined>(undefined);

  async function init(): Promise<void> {
    const initialAccountId = await NBXStorage.getItem('active-account');
    setAccountId(initialAccountId);
  }

  useEffect(() => {
    function accountIdChanged(e): void {
      setAccountId(e.detail);
    }

    init();
    window.addEventListener(authTypes.AUTH_ACCOUNT_ID_CHANGED, accountIdChanged);
    return (): void => window.removeEventListener(authTypes.AUTH_ACCOUNT_ID_CHANGED, accountIdChanged);
  }, []);

  return accountId;
}
export function useAuthUserAccounts(): Array<UserAccount> {
  const [userAccounts, setUserAccounts] = useState<Array<UserAccount>>([]);

  async function init(): Promise<void> {
    const userAccountsFromStorage = await NBXStorage.getItem('user-accounts');
    const initialUserAccounts = userAccountsFromStorage ? JSON.parse(userAccountsFromStorage) : [];
    setUserAccounts(initialUserAccounts);
  }

  useEffect(() => {
    function userAccountsChanged(e): void {
      setUserAccounts(e.detail);
    }

    init();
    window.addEventListener(authTypes.AUTH_USER_ACCOUNTS_CHANGED, userAccountsChanged);
    return (): void => window.removeEventListener(authTypes.AUTH_USER_ACCOUNTS_CHANGED, userAccountsChanged);
  }, []);

  return userAccounts;
}
export function useNotifications(handleNewNotification): Array<NotificationInternalParams> {
  const [notifications, setNotifications] = useState<any[]>([]);
  const notificationReceived = useCallback(
    e => {
      const date = new Date();
      const id = date.getTime();
      const ttl = e.detail.timeout;
      const newNotification = { ...e.detail, ttl, id };
      setNotifications([...notifications, newNotification]);
      handleNewNotification?.(newNotification);
    },
    [notifications, handleNewNotification]
  );
  const notificationRemoved = useCallback(
    e => {
      const id = e.detail;
      setNotifications(notifications.filter(notification => notification.id !== id));
    },
    [notifications]
  );
  useEffect(() => {
    window.addEventListener(uiTypes.UI_SET_NOTIFICATION, notificationReceived);
    window.addEventListener(uiTypes.UI_UNSET_NOTIFICATION, notificationRemoved);
    return (): void => {
      window.removeEventListener(uiTypes.UI_SET_NOTIFICATION, notificationReceived);
      window.removeEventListener(uiTypes.UI_UNSET_NOTIFICATION, notificationRemoved);
    };
  }, [notificationReceived, notificationRemoved]);
  return notifications;
}
export function useTheme(): string | null | undefined {
  const [theme, setTheme] = useState<string | null | undefined>(undefined);

  async function init(): Promise<void> {
    const initialTheme = await NBXStorage.getItem('theme', { useLocalStorage: true });
    setTheme(initialTheme);
  }

  useEffect(() => {
    function themeChanged(e): void {
      setTheme(e.detail);
    }

    init();
    window.addEventListener(uiTypes.UI_THEME_CHANGED, themeChanged);
    return (): void => window.removeEventListener(uiTypes.UI_THEME_CHANGED, themeChanged);
  }, [theme]);

  return theme;
}

export function useLang(): string | null | undefined {
  const [lang, setLang] = useState<string | null | undefined>(undefined);

  async function init(): Promise<void> {
    const initialLang = await NBXStorage.getItem('i18nextLng', { useLocalStorage: true });
    setLang(initialLang);
  }

  useEffect(() => {
    function langChanged(e): void {
      setLang(e.detail);
    }

    init();
    window.SUBSCRIBE(LANG, langChanged);
    return (): void => window.UNSUBSCRIBE(LANG, langChanged);
  }, [lang]);

  return lang;
}

export function useIsCorporateAccount(): boolean {
  const userAccounts = useAuthUserAccounts();
  const accountId = useAuthAccountId();
  return !!userAccounts?.find(account => account.id === accountId)?.name;
}
